import React, { useState, useEffect } from 'react';
import axios from '../../utils/axiosInstance';
import CommonForm from '../../components/commonComponent/CommonForm';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import { useNavigate, useParams } from "react-router-dom";

const Vanuecreate = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Setup default values from localStorage if available
  const defaultCountry = localStorage.getItem('defaultCountry') || '';
  const defaultState = localStorage.getItem('defaultState') || '';
  const defaultCity = localStorage.getItem('defaultCity') || '';

  const [formValues, setFormValues] = useState({
    name: '',
    hollNo: '', // backend expects hollNo
    hotelName: '',
    area: '',
    landmark: '',
    city: defaultCity,
    state: defaultState,
    country: defaultCountry,
    pincode: '',
    contactPersonName: '', // backend expects contactPersonName
    contactPersonMobileNumber: '', // backend expects contactPersonMobileNumber
    googleLink: '',
    fees: '',
  });

  const [errors, setErrors] = useState({});
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  // Fetch countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await axios.get('/admin/api/country');
        setCountryOptions(res.data?.countries?.map(c => ({ value: c._id, label: c.name })) || []);
      } catch (err) {
        console.error(err);
        toastError('Failed to load countries');
      }
    };
    fetchCountries();
  }, []);

  // Fetch states when country changes, and select default or edit value
  useEffect(() => {
    if (!formValues.country) {
      setStateOptions([]);
      setCityOptions([]);
      setFormValues(prev => ({ ...prev, state: '', city: '' }));
      return;
    }

    const fetchStates = async () => {
      try {
        const res = await axios.get(`/admin/api/state?countryId=${formValues.country}`);
        const options = res.data?.states?.map(s => ({ value: s._id, label: s.name })) || [];
        setStateOptions(options);

        // If state is not set, select defaultState if available
        if (!formValues.state && defaultState && options.find(s => s.value === defaultState)) {
          setFormValues(prev => ({ ...prev, state: defaultState }));
        }
      } catch (err) {
        console.error(err);
        toastError('Failed to load states');
      }
    };
    fetchStates();
  }, [formValues.country]);

  // Fetch cities when state changes, and select default or edit value
  useEffect(() => {
    if (!formValues.state) {
      setCityOptions([]);
      setFormValues(prev => ({ ...prev, city: '' }));
      return;
    }

    const fetchCities = async () => {
      try {
        const res = await axios.get(`/admin/api/city?stateId=${formValues.state}`);
        const options = res.data?.cities?.map(c => ({ value: c._id, label: c.name })) || [];
        setCityOptions(options);

        // If city is not set, select defaultCity if available
        if (!formValues.city && defaultCity && options.find(c => c.value === defaultCity)) {
          setFormValues(prev => ({ ...prev, city: defaultCity }));
        }
      } catch (err) {
        console.error(err);
        toastError('Failed to load cities');
      }
    };
    fetchCities();
  }, [formValues.state]);

  // Fetch venue data for edit
  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      try {
        const res = await axios.get(`/admin/api/venues/${id}`);
        const venue = res.data?.venue;

        if (!venue) throw new Error('Venue not found');

        // Use _id for country, state, city, fallback to default if missing
        const countryId = venue.country?._id || venue.country || defaultCountry;
        const stateId = venue.state?._id || venue.state || defaultState;
        const cityId = venue.city?._id || venue.city || defaultCity;

        setFormValues({
          name: venue.venueName || venue.name || '',
          hollNo: venue.hollNo || '',
          hotelName: venue.hotelName || '',
          area: venue.area ||'',
          landmark: venue.landmark ||'',
          city: cityId,
          state: stateId,
          country: countryId,
          pincode: venue.pincode || '',
          contactPersonMobileNumber: venue.contactPersonMobileNumber || '',
          contactPersonName: venue.contactPersonName || '',
          googleLink: venue.googleLink || '',
          fees: venue.fees ?? '',
        });

        // Fetch state and city dropdowns for edit values
        if (countryId) {
          try {
            const stateRes = await axios.get(`/admin/api/state?countryId=${countryId}`);
            setStateOptions(stateRes.data?.states?.map(s => ({ value: s._id, label: s.name })) || []);
          } catch (err) {}
        }
        if (stateId) {
          try {
            const cityRes = await axios.get(`/admin/api/city?stateId=${stateId}`);
            setCityOptions(cityRes.data?.cities?.map(c => ({ value: c._id, label: c.name })) || []);
          } catch (err) {}
        }
      } catch (error) {
        console.error(error);
        toastError('Failed to fetch venue data');
        navigate('/vanue');
      }
    };

    fetchData();
    // eslint-disable-next-line
  }, [id, navigate]);

  const handleSubmit = async () => {
    const validationRules = {
      name: { required: true },
      hollNo: { required: true },
      hotelName: { required: true },
      contactPersonMobileNumber: { required: true },
      contactPersonName: { required: true },
      fees: { required: true },
      googleLink: { required: true },
    };

    const validationErrors = validateForm(formValues, validationRules);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const payload = {
      name: formValues.name,
      hollNo: formValues.hollNo,
      hotelName: formValues.hotelName,
      landmark: formValues.landmark,
      area: formValues.area,
      country: formValues.country,
      state: formValues.state,
      city: formValues.city,
      pincode: formValues.pincode,
      contactPersonName: formValues.contactPersonName,
      contactPersonMobileNumber: formValues.contactPersonMobileNumber,
      googleLink: formValues.googleLink,
      fees: formValues.fees,
    };

    try {
      if (id) {
        await axios.put(`/admin/api/venues/${id}`, payload);
        toastSuccess('Venue updated successfully!');
      } else {
        await axios.post('/admin/api/venues', payload);
        toastSuccess('Venue created successfully!');
      }
      navigate('/vanue');
    } catch (error) {
      console.error(error?.response?.data || error.message);
      toastError('Failed to save venue');
    }
  };

  const handleCancel = () => {
    setFormValues({
      name: '',
      hollNo: '',
      hotelName: '',
      landmark: '',
      area: '',
      city: defaultCity,
      state: defaultState,
      country: defaultCountry,
      pincode: '',
      contactPersonName: '',
      contactPersonMobileNumber: '',
      googleLink: '',
      fees: '',
    });
    setErrors({});
  };

  const fields = [
    { name: 'name', label: 'Vanue Name', placeholder: 'Enter name', type: 'text' },
    { name: 'hollNo', label: 'Hall No', placeholder: 'Enter Hall No', type: 'text' },
    { name: 'hotelName', label: 'Hotel Name', placeholder: 'Enter Hotel Name', type: 'text' },
    { name: 'landmark', label: 'Landmark', placeholder: 'Enter landmark', type: 'text' },
    { name: 'area', label: 'Area', placeholder: 'Enter area', type: 'textarea', rows: 2 },
    { name: 'country', label: 'Country', placeholder: 'Select country', type: 'select', options: countryOptions },
    { name: 'state', label: 'State', placeholder: 'Select state', type: 'select', options: stateOptions },
    { name: 'city', label: 'City', placeholder: 'Select city', type: 'select', options: cityOptions },
    { name: 'pincode', label: 'Pincode', placeholder: 'Enter Pincode', type: 'text' },
    { name: 'contactPersonName', label: 'Contact Person', placeholder: 'Enter contact person', type: 'text' },
    { name: 'contactPersonMobileNumber', label: 'Contact Number', placeholder: 'Enter contact number', type: 'text' },
    { name: 'googleLink', label: 'Location Link', placeholder: 'Enter Google Maps link', type: 'text' },
    { name: 'fees', label: 'Fees', placeholder: 'Enter fees', type: 'number' },
  ];

  return (
    <div className="container mt-4">
      <h4 className="mb-3 text-dark border-bottom pb-2">
        {id ? 'Edit Venue' : 'Create Venue'}
      </h4>

      <CommonForm
        title="Venue Form"
        fields={fields}
        values={formValues}
        onChange={(updated) => setFormValues(prev => ({ ...prev, ...updated }))}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        errors={errors}
        requiredFields={['name', 'hollNo', 'hotelName', 'contactPersonMobileNumber', 'contactPersonName', 'fees', 'googleLink']}
        submitLabel={id ? 'Update' : 'Save'}
        cancelLabel="Reset"
      />
    </div>
  );
};

export default Vanuecreate;